import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, reactive, ref } from 'vue';
import { useRoute, useRouter } from "vue-router/composables";
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import FiltersDesign, { type FilterConfig } from "@/components_v3/filters/FiltersDesign.vue";
import { getActiveUserSearch, getAffiliateSearch, getUserDepartmentsV3 } from "@/api/user";
import {
    getEmployeesActiveProducts,
    getEmployeesCountries,
    getEmployeesDepartments,
    getEmployeesOffersV2, getEmployeesStats,
    getEmployeesTargetActions,
    getOfferTags,
    getOneEmployeesOffer,
} from "@/api/offers";
import { affiliateStatuses, offerStatuses } from "@/mappings/statuses";
import { translateOffersStatus, translatePartnerStatus } from "@/utils/translate";
import store from "@/store";
import { useHoldSingleStore } from "@/store_v3/HoldSingleStore";
import { IHoldSummary } from "@crm-ui/types";
import BaseListItem from "@/components-UI/list/BaseListItem.vue";
import BaseList from "@/components-UI/list/BaseList.vue";
import { HoldFilters } from "@/services/TablePage/hold";
import { showServerError } from "@/utils";
import { getAdvertisersSearch } from "@/api/revenue";

type FilterSection =
    | { title: string; type: 'totals' }
    | { title: string; type?: 'filters'; filters: string[], class?: string };


export default /*#__PURE__*/_defineComponent({
  __name: 'HoldFilters',
  setup(__props, { expose }) {

const route = useRoute();
const router = useRouter();
const holdSingleStore = useHoldSingleStore();
const filterClass = new HoldFilters();

/** Ключи query, которые не относятся к фильтрам холда */
const QUERY_KEYS_SKIP = new Set(['columns', 'offset', 'tab', 'sort_by', 'sort_desc']);

/** Фильтры, в query приходят как числовые id */
const NUMERIC_FILTER_KEYS = new Set([
    'master_id',
    'affiliate_id',
    'master_manager_id',
    'master_manager_department_id',
    'advertiser_id',
    'advertiser_manager_id',
    'offer_id',
    'offer_country',
    'offer_goal_id',
    'offer_department',
    'offer_product',
    'offer_target_action',
    'offer_stats',
    'offer_tag',
]);

const splitQueryValues = (raw: string | (string | null)[] | null | undefined): string[] => {
    if (raw === undefined || raw === null || raw === '') return [];
    const parts = Array.isArray(raw) ? raw : [raw];
    return parts
        .filter((x): x is string => x != null && x !== '')
        .flatMap((x) => x.split(',').map((s) => s.trim()).filter(Boolean));
};

const coerceFilterValue = (key: string, strings: string[]): (string | number)[] | '' => {
    if (!strings.length) return '';
    const mapOne = (v: string) =>
        NUMERIC_FILTER_KEYS.has(key) && v !== '' && !Number.isNaN(Number(v)) ? Number(v) : v;
    return strings.map(mapOne);
};

const applyFiltersFromRoute = (): void => {
    const q = route.query;
    const fromStr = Array.isArray(q.date_from) ? q.date_from[0] : q.date_from;
    const toStr = Array.isArray(q.date_to) ? q.date_to[0] : q.date_to;
    filterValues.date = [fromStr, toStr];

    for (const key of Object.keys(q)) {
        if (QUERY_KEYS_SKIP.has(key) || key === 'date_from' || key === 'date_to') continue;
        if (!(key in filterValues)) continue;
        const strings = splitQueryValues(q[key]);
        if (!strings.length) continue;
        if (key === 'offer_currency') {
            filterValues[key] = strings[0];
            continue;
        }
        filterValues[key] = coerceFilterValue(key, strings);
    }
};

const columns: FilterSection[][] = [
    [
        { title: 'Период и валюта', filters: ['date', 'offer_currency'], class: 'date-cur' },
        { title: 'Итого', type: 'totals' },
    ],
    [
        {
            title: 'Партнёр',
            filters: ['master_id', 'affiliate_id', 'affiliate_status', 'master_manager_id', 'master_manager_department_id'],
        },
        { title: 'Рекламодатель', filters: ['advertiser_id', 'advertiser_status', 'advertiser_manager_id'] },
    ],
    [
        {
            title: 'Оффер',
            filters: ['offer_id', 'offer_status', 'offer_goal_id', 'offer_goal_status', 'offer_department', 'offer_country', 'offer_product', 'offer_target_action', 'offer_stats', 'offer_tag'],
        },
    ],
];

const totalItems: { label: string; key: keyof IHoldSummary }[] = [
    { label: '∑ новых принятых', key: 'new_approved_payout' },
    { label: '∑ старых принятых', key: 'old_approved_payout' },
    { label: '∑ корректировок', key: 'adjust_payout' },
    { label: '∑ вышедших из холда', key: 'hold_expired_payout' },
    { label: '∑ итого', key: 'total_payout' },
];

/** Не отправляем в стор ключи с null, '', [] и массивами из одних пустых ячеек (например дата [undefined, undefined]). */
const isEmptyFilterValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) {
        if (!value.length) return true;
        return value.every((v) => isEmptyFilterValue(v));
    }
    return false;
};

const filterValues = reactive<Record<string, any>>({
    date: null,
    offer_currency: 'RUB',
    master_id: null,
    affiliate_id: null,
    affiliate_status: null,
    master_manager_id: null,
    master_manager_department_id: null,
    advertiser_id: null,
    advertiser_status: null,
    advertiser_manager_id: null,
    offer_id: null,
    offer_status: null,
    offer_goal_id: null,
    offer_goal_status: null,
    offer_department: null,
    offer_country: null,
    offer_product: null,
    offer_target_action: null,
    offer_stats: null,
    offer_tag: null,
});

const syncFiltersToStore = () => {
    const rawFilters = Object.fromEntries(
        Object.entries(filterValues).filter(([key, value]) => Boolean(key) && !isEmptyFilterValue(value)),
    );
    const prepared = filterClass.prepareFiltersForSend(rawFilters);
    const filters = prepared.filter((item) => !isEmptyFilterValue(item.values));
    holdSingleStore.updateFilters(filters);
};

const normalizeQueryPrimitive = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') {
        const v = value.trim();
        return v ? v : null;
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return null;
};

const toQueryValue = (value: unknown): string | undefined => {
    if (isEmptyFilterValue(value)) return undefined;
    if (Array.isArray(value)) {
        const parts = value
            .map((item) => normalizeQueryPrimitive(item))
            .filter((item): item is string => Boolean(item));
        return parts.length ? parts.join(',') : undefined;
    }
    return normalizeQueryPrimitive(value) ?? undefined;
};

const serializeQuery = (query: Record<string, unknown>): string => Object.keys(query)
    .sort()
    .map((key) => {
        const raw = query[key];
        const values = Array.isArray(raw) ? raw : [raw];
        const normalized = values
            .map((item) => normalizeQueryPrimitive(item))
            .filter((item): item is string => Boolean(item));
        return `${key}=${normalized.join(',')}`;
    })
    .join('&');

const syncFilterValuesToRoute = (): void => {
    const nextQuery: Record<string, unknown> = { ...route.query };

    delete nextQuery.date_from;
    delete nextQuery.date_to;
    for (const key of Object.keys(filterValues)) {
        if (key !== 'date') delete nextQuery[key];
    }

    const [dateFrom, dateTo] = Array.isArray(filterValues.date) ? filterValues.date : [undefined, undefined];
    const dateFromQuery = toQueryValue(dateFrom);
    const dateToQuery = toQueryValue(dateTo);

    if (dateFromQuery !== undefined) nextQuery.date_from = dateFromQuery;
    if (dateToQuery !== undefined) nextQuery.date_to = dateToQuery;

    for (const [key, value] of Object.entries(filterValues)) {
        if (key === 'date') continue;
        const queryValue = toQueryValue(value);
        if (queryValue !== undefined) nextQuery[key] = queryValue;
    }

    if (serializeQuery(route.query as Record<string, unknown>) === serializeQuery(nextQuery)) return;
    void router.replace({ query: nextQuery });
};

const formatAmount = (value: number): string =>
    new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

const getTotal = (key: keyof IHoldSummary) => {
    const currency = filterValues.offer_currency;
    if (currency === null) return '';
    const summary = holdSingleStore.totals[0];
    if (!summary) return '';
    return `${formatAmount(Number(summary[key]))} ${currency}`;
};

const selectItems = ref<Record<string, unknown[]>>({});

const loadingDataForSelect = [
    { id: 'offer_country', errorName: 'Список стран не загружен', api: getEmployeesCountries },
    { id: 'offer_product', errorName: 'Список типов продуктов не загружен', api: getEmployeesActiveProducts },
    { id: 'offer_target_action', errorName: 'Список целевых действий не загружен', api: getEmployeesTargetActions },
    { id: 'offer_tag', errorName: 'Список тегов не загружен', api: getOfferTags },
    { id: 'offer_stats', errorName: 'Список типов статистики не загружен', api: getEmployeesStats },
    { id: 'offer_department', errorName: 'Список направлений не загружен', api: getEmployeesDepartments },
    {
        id: 'master_manager_department_id',
        errorName: 'Список отделов не загружен',
        api: getUserDepartmentsV3,
    },
];

const setDataForSelect = () => {
    for (const select of loadingDataForSelect) {
        getDataForSelect(select.api, select.id, select.errorName);
    }
};

/** Ответы часто уже развёрнуты в `data`, но оставляем запасной путь для «сырых» ответов. */
const normalizeLoadedItems = (raw: unknown): unknown[] => {
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'object' && 'data' in (raw as object)) {
        const nested = (raw as { data: unknown }).data;
        if (Array.isArray(nested)) return nested;
    }
    return [];
};

const getLoadedSelectItems = (id: string): unknown[] => selectItems.value[id] ?? [];

const getDataForSelect = async (apiMethod: any, filterId: string, errorName: string = 'Список не загружен'): Promise<void> => {
    try {
        const items = await apiMethod();
        const list = normalizeLoadedItems(items);
        selectItems.value = { ...selectItems.value, [filterId]: list };
    } catch (err) {
        showServerError(err, errorName);
    }
};

const fetchOfferFromRoute = async (): Promise<void> => {
    const raw = route.query.offer_id;
    const rawStr = Array.isArray(raw) ? raw[0] : raw;
    if (!rawStr) return;
    const id = Number(rawStr.split(',')[0]);
    if (!id || Number.isNaN(id)) return;
    try {
        const offer = await getOneEmployeesOffer(id);
        holdSingleStore.updateOffer(offer);
    } catch (err) {
        showServerError(err, 'Данные оффера не загружены');
    }
};

onBeforeMount(() => {
    setDataForSelect();
    applyFiltersFromRoute();
    syncFiltersToStore();
    fetchOfferFromRoute();
});

const updateFilter = (key: string, value: any) => {
    filterValues[key] = value;
    syncFiltersToStore();
    syncFilterValuesToRoute();
};

const currencies = computed(() => store.getters['catalogueModule/GET_CURRENCIES'] ?? []);

const statusItems = (list: string[], translator: (s: string) => { text: string }) =>
    list.map((value) => ({ value, name: translator(value).text }));

const affiliateStatusItems = statusItems(affiliateStatuses, translatePartnerStatus);
const offerStatusItems = statusItems(offerStatuses, translateOffersStatus);

const advertiserStatusItems = [
    { value: 'active', name: 'Активен' },
    { value: 'pending', name: 'В ожидании' },
    { value: 'deleted', name: 'Удалён' },
];

const goalStatusItems = [
    { value: 'active', name: 'Активна' },
    { value: 'deleted', name: 'Удалена' },
];

const selectDefaults: Partial<FilterConfig> = {
    type: 'select',
    multiple: true,
    filterable: true,
    clearable: true,
    view: 'border-bottom',
    size: 'medium',
};

const autocompleteDefaults: Partial<FilterConfig> = {
    type: 'autocomplete',
    multiple: true,
    isMultipaste: true,
    clearable: true,
    view: 'border-bottom',
    size: 'medium',
    triggerOnMounted: true,
};

const select = (overrides: Partial<FilterConfig>): FilterConfig =>
    ({ ...selectDefaults, ...overrides }) as FilterConfig;

const autocomplete = (overrides: Partial<FilterConfig>): FilterConfig =>
    ({ ...autocompleteDefaults, ...overrides }) as FilterConfig;

const filterConfigs = computed<Record<string, FilterConfig>>(() => ({
    date: {
        type: 'datetime',
        label: 'Период',
        format: 'YYYY-MM-DD',
        view: 'outlined',
        size: 'medium',
        range: true,
        clearable: false,
    },
    offer_currency: select({
        label: 'Валюта',
        items: currencies.value,
        itemValue: 'code',
        itemLabel: 'code',
        clearable: false,
        multiple: false,
        placeholder: 'Выберите валюту',
    }),
    master_id: autocomplete({
        label: 'Мастер',
        apiMethod: getAffiliateSearch,
        itemValue: 'id',
        itemLabel: 'text',
        template: (item) => `${item.id}`,
        placeholder: 'Выберите мастера',
    }),
    affiliate_id: autocomplete({
        label: 'Партнёр',
        apiMethod: getAffiliateSearch,
        itemValue: 'id',
        itemLabel: 'name',
        template: ({ id, first_name, last_name, email }) => `${id} ${first_name} ${last_name} ${email}`,
        placeholder: 'Выберите партёра',
    }),
    affiliate_status: select({
        label: 'Статус партнёра',
        items: affiliateStatusItems,
        itemValue: 'value',
        itemLabel: 'name',
        placeholder: 'Выберите статус партнёра',
    }),
    master_manager_id: autocomplete({
        label: 'Менеджер',
        apiMethod: getActiveUserSearch,
        itemValue: 'id',
        itemLabel: 'name',
        template: (item) => `${item.id} ${item.first_name} ${item.last_name}`,
        placeholder: 'Выберите менеджера',
    }),
    master_manager_department_id: select({
        label: 'Отдел менеджера',
        items: getLoadedSelectItems('master_manager_department_id'),
        itemValue: 'id',
        itemLabel: 'name',
        placeholder: 'Выберите отдел менеджера',
    }),
    advertiser_id: autocomplete({
        label: 'Рекламодатель',
        apiMethod: getAdvertisersSearch,
        itemValue: 'id',
        itemLabel: 'name',
        searchKey: 'query',
        placeholder: 'Выберите рекламодателя',
    }),
    advertiser_status: select({
        label: 'Статус рекла',
        items: advertiserStatusItems,
        itemValue: 'value',
        itemLabel: 'name',
        placeholder: 'Выберите статус рекла',
    }),
    advertiser_manager_id: autocomplete({
        label: 'Менеджер',
        apiMethod: getActiveUserSearch,
        itemValue: 'id',
        itemLabel: 'name',
        template: (item) => `${item.id} ${item.first_name} ${item.last_name}`,
        placeholder: 'Выберите менеджера',
    }),
    offer_id: autocomplete({
        label: 'Оффер',
        apiMethod: getEmployeesOffersV2,
        itemValue: 'legacy_id',
        itemLabel: 'name',
        template: (item) => `${item.legacy_id} ${item.name}`,
        placeholder: 'Выберите оффера',
    }),
    offer_status: select({
        label: 'Статус оффера',
        items: offerStatusItems,
        itemValue: 'value',
        itemLabel: 'name',
        placeholder: 'Выберите статус оффера',
    }),
    offer_goal_id: select({
        label: 'Цель',
        items: holdSingleStore.offer?.goals ?? [],
        itemValue: 'legacy_id',
        itemLabel: 'name',
        disabled: !holdSingleStore.offer?.goals?.length,
        template: ({ legacy_id, name }) => `${legacy_id} ${name}`,
        placeholder: 'Выберите цель',
    }),
    offer_goal_status: select({
        label: 'Статус цели',
        items: goalStatusItems,
        itemValue: 'value',
        itemLabel: 'name',
        placeholder: 'Выберите статус цели',
    }),
    offer_department: select({
        label: 'Направление',
        items: getLoadedSelectItems('offer_department'),
        itemValue: 'id',
        itemLabel: 'name',
        placeholder: 'Выберите направление',
    }),
    offer_country: select({
        label: 'Страна',
        itemValue: 'id',
        itemLabel: 'name',
        items: getLoadedSelectItems('offer_country'),
        placeholder: 'Выберите страны',
    }),
    offer_product: select({
        label: 'Тип продукта',
        items: getLoadedSelectItems('offer_product'),
        itemValue: 'id',
        itemLabel: 'name',
        placeholder: 'Выберите тип продукта',
    }),
    offer_target_action: select({
        label: 'Целевое действие',
        items: getLoadedSelectItems('offer_target_action'),
        itemValue: 'id',
        itemLabel: 'name',
        placeholder: 'Выберите целевое действие',
    }),
    offer_stats: select({
        label: 'Тип статистики',
        items: getLoadedSelectItems('offer_stats'),
        itemValue: 'id',
        itemLabel: 'name',
        placeholder: 'Выберите тип статистики',
    }),
    offer_tag: select({
        label: 'Теги оффера',
        items: getLoadedSelectItems('offer_tag'),
        itemValue: 'id',
        itemLabel: 'name',
        placeholder: 'Выберите теги оффера',
    }),
}));

const getFilterWithoutLabel = (key: string): FilterConfig => {
    const { label, ...rest } = filterConfigs.value[key];
    return rest as FilterConfig;
};

expose({ filterValues });

return { __sfc: true,route, router, holdSingleStore, filterClass, QUERY_KEYS_SKIP, NUMERIC_FILTER_KEYS, splitQueryValues, coerceFilterValue, applyFiltersFromRoute, columns, totalItems, isEmptyFilterValue, filterValues, syncFiltersToStore, normalizeQueryPrimitive, toQueryValue, serializeQuery, syncFilterValuesToRoute, formatAmount, getTotal, selectItems, loadingDataForSelect, setDataForSelect, normalizeLoadedItems, getLoadedSelectItems, getDataForSelect, fetchOfferFromRoute, updateFilter, currencies, statusItems, affiliateStatusItems, offerStatusItems, advertiserStatusItems, goalStatusItems, selectDefaults, autocompleteDefaults, select, autocomplete, filterConfigs, getFilterWithoutLabel, BaseEditableCard, FiltersDesign, BaseListItem, BaseList }
}

})